import { LibraryVideo, VideoType } from './videos.domain';
import { Async, SMap } from '@client/new/helpers/types';
import { useGetCreatorsMediaQuery } from 'generated/graphql';
import { resultsToAsync } from '@client/new/helpers/gql';
import { makeAsync } from '@client/new/helpers/async';
import { useState } from 'react';

import {
  toLibraryVideo,
  uploadVideo,
  UploadVideoParams,
} from './videos.helpers';
import {
  allowedVideoTypes,
  maxFileSizeBytes,
  maxFileSizeMB,
} from './videos.constants';

export type UseVideosParams = {
  influencerId: number;
};

type Videos = Array<LibraryVideo>;
type UseVideosResult = {
  data: Async<Videos>;
  refetch: () => Promise<void>;
  refetchStatus: Async<null>['type'];
};

export const useVideos = ({
  influencerId,
}: UseVideosParams): UseVideosResult => {
  const [refetching, setRefetching] = useState<Async<null>>(
    makeAsync.notFetched,
  );
  const [_, setAdditionalVideos] = useState<Videos>([]);

  const videosQuery = useGetCreatorsMediaQuery({
    variables: { creatorId: influencerId },
  });

  const asyncResult = resultsToAsync([videosQuery]);

  const refetch = () => {
    setRefetching(makeAsync.fetching());
    return Promise.all([videosQuery.refetch()])
      .then(() => setRefetching(makeAsync.fetched(null)))
      .catch((err) => setRefetching(makeAsync.error(err)))
      .finally(() => setAdditionalVideos([]));
  };

  const mappedVideo = videosQuery.data?.getCreatorsMedia
    ? videosQuery.data.getCreatorsMedia.map(toLibraryVideo('GQLVideo'))
    : [];

  const data =
    asyncResult.type === 'fetched'
      ? makeAsync.fetched<Videos>(
          mappedVideo, //[g] TODO: fix this
        )
      : asyncResult;

  return { refetchStatus: refetching.type, refetch, data };
};

export function useShortVideos(_p: UseVideosParams): Async<LibraryVideo[]> {
  return { type: 'not-fetched' }; // [g] TODO: remove all dependencies
}

export type UploadState = {
  progress: number;
  fileName: string;
  fileId: string;
  videoType: VideoType;
  state: Async<LibraryVideo>;
};

export type UploadMap = SMap<UploadState>;

type UploadFileParams = Pick<UploadVideoParams, 'file' | 'editParams'> & {
  videoType: VideoType;
};

const toFileId = (file: File) => `${file.name}-${file.size}-${Date.now()}`;

export const uploadFile =
  (p: {
    uploadMap: UploadMap;
    setUploadMap: React.Dispatch<React.SetStateAction<UploadMap>>;
    influencerId: number;
  }) =>
  async ({ file, videoType, editParams }: UploadFileParams) => {
    const fileId = toFileId(file);
    const updateState = (delta: Partial<UploadState>) => {
      let nextState: UploadState = { ...p.uploadMap[fileId], ...delta };
      p.setUploadMap((prev) => {
        nextState = { ...prev[fileId], ...delta };
        return { ...prev, [fileId]: nextState };
      });
      return nextState;
    };

    updateState({
      fileId,
      fileName: file.name,
      progress: 0,
      videoType,
      state: makeAsync.notFetched(),
    });

    if (!allowedVideoTypes.includes(file.type))
      return updateState({ state: makeAsync.error('invalid file type.') });

    if (file.size > maxFileSizeBytes)
      return updateState({
        state: makeAsync.error(
          `size exceeds the maximum allowed of ${maxFileSizeMB}MB.`,
        ),
      });

    try {
      updateState({ state: makeAsync.fetching() });
      const video = await uploadVideo(videoType)({
        file,
        influencerId: p.influencerId,
        onProgress: (value) => updateState({ progress: Math.round(value) }),
        editParams,
      });
      return updateState({ state: makeAsync.fetched(video) });
    } catch (error) {
      console.error('Upload error', error);
      return updateState({
        state: makeAsync.error('an error occurred while uploading the file.'),
      });
    }
  };

export const useVideosUpload = (p: UseVideosParams) => {
  const [uploadMap, setUploadMap] = useState<UploadMap>({});
  const clearUploadMapByFileId = (fileId: UploadState['fileId']) =>
    setUploadMap((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });

  return {
    uploadFile: uploadFile({ setUploadMap, uploadMap, ...p }),
    uploadMap,
    clearUploadMapByFileId,
  };
};
